// Typ: FindAreaFromTwoSidesAndAngleTask
// Wzór: P = 1/2 * a * b * sin(gamma)

import { InlineMath, BlockMath } from "react-katex";
import RootFractionInput, {
  parseRootFractionInput,
  formatLatexPreview
} from "../../../../components/inputs/RootFractionInput";

// === utils ===
const rnd = (a,b)=> Math.floor(Math.random()*(b-a+1))+a;

// sin wartości „ładnych” kątów (symbolicznie i numerycznie)
const SIN_INFO = {
  30:  { num: 1, den: 2, root: null,  latex: "\\tfrac{1}{2}" },
  45:  { num: 1, den: 2, root: 2,     latex: "\\tfrac{\\sqrt{2}}{2}" },
  60:  { num: 1, den: 2, root: 3,     latex: "\\tfrac{\\sqrt{3}}{2}" },
  120: { num: 1, den: 2, root: 3,     latex: "\\tfrac{\\sqrt{3}}{2}" }, // = sin 60
  135: { num: 1, den: 2, root: 2,     latex: "\\tfrac{\\sqrt{2}}{2}" }, // = sin 45
  150: { num: 1, den: 2, root: null,  latex: "\\tfrac{1}{2}" },         // = sin 30
};

function gcd(a, b) {
  a = Math.abs(a); b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

// format: (k/den) * sqrt(root?)  => LaTeX
// — bez pierwiastka zwraca ładny zapis: całkowita / ułamek właściwy / LICZBA MIESZANA
function formatCoeffWithRoot(k, den, root) {
  const d = gcd(Math.abs(k), Math.abs(den));
  let num = k / d;
  let denS = den / d;

  if (!root) {
    const sign = num < 0 ? "-" : "";
    const absNum = Math.abs(num);

    if (denS === 1) return `${num}`; // całkowita
    if (absNum < denS) {
      // ułamek właściwy
      return `${sign}\\dfrac{${absNum}}{${denS}}`;
    }
    // liczba mieszana
    const whole = Math.floor(absNum / denS);
    const rem = absNum % denS;
    if (rem === 0) return `${sign}${whole}`;
    return `${sign}${whole}\\dfrac{${rem}}{${denS}}`;
  }

  // z pierwiastkiem
  const sign = num < 0 ? "-" : "";
  const nabs = Math.abs(num);

  if (nabs === 1 && denS === 1) return `${sign}\\sqrt{${root}}`;
  if (denS === 1) return `${sign}${nabs}\\sqrt{${root}}`;
  if (nabs === 1) return `${sign}\\dfrac{\\sqrt{${root}}}{${denS}}`;
  return `${sign}\\dfrac{${nabs}\\sqrt{${root}}}{${denS}}`;
}

// === generator ===
export function generateTask(options = {}) {
  // wybór pary boków i odpowiedniego kąta między nimi
  const variants = [
    { b1: "a", b2: "b", angleName: "γ" }, // gamma między a i b
    { b1: "b", b2: "c", angleName: "α" }, // alpha między b i c
    { b1: "c", b2: "a", angleName: "β" }, // beta między c i a
  ];
  const v = variants[rnd(0, variants.length - 1)];

  // kąt z puli 30..150 bez 90
  const angles = [30,45,60,120,135,150];
  const allowedAngles = Array.isArray(options.allowedAngles)
    ? options.allowedAngles
        .map(Number)
        .filter((candidate) => angles.includes(candidate))
    : angles;
  const anglePool = allowedAngles.length ? allowedAngles : angles;
  const requestedAngle = Number(options.forceAngle);
  const angle = angles.includes(requestedAngle)
    ? requestedAngle
    : anglePool[rnd(0, anglePool.length - 1)];

  // ładne długości boków
  const x = rnd(6, 24);
  const y = rnd(5, 22);

  return {
    type: "FindAreaFromTwoSidesAndAngleTask",
    data: {
      b1Name: v.b1, b2Name: v.b2, angleName: v.angleName,
      b1Val: x, b2Val: y, angle
    }
  };
}

// === render task ===
export function renderTask({ data }) {
  const { b1Name, b2Name, angleName, b1Val, b2Val, angle } = data;
  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        W trójkącie <InlineMath math={'ABC'} /> dane są boki
        <InlineMath math={`\\;${b1Name}=${b1Val}`} /> oraz
        <InlineMath math={`\\;${b2Name}=${b2Val}`} /> oraz kąt
        <InlineMath math={`\\;${angleName}=${angle}^\\circ`} /> zawarty między tymi bokami.
        Oblicz pole trójkąta <InlineMath math={'P'} />.
      </div>
    </div>
  );
}

// === input ===
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div style={{ display:"grid", gap: 10, marginTop: 8 }}>
      <RootFractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label={'P'}
        placeholder="np. (13√3)/4, √2/8, 15/2"
      />
    </div>
  );
}

// === check ===
export function checkAnswer({ data }, userAnswer) {
  const { b1Val, b2Val, angle } = data;
  const s = SIN_INFO[angle];
  if (!s) return false;

  const expected =
    0.5 * b1Val * b2Val * (s.root ? Math.sqrt(s.root) / s.den : s.num / s.den);

  // 1) standardowy parse z RootFractionInput (√, ułamki itp.)
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  let parsed =
    typeof userAnswer === "object" && userAnswer.parsed != null
      ? userAnswer.parsed
      : parseRootFractionInput(raw);

  // 2) fallback: liczba z przecinkiem dziesiętnym (np. "131,64")
  if ((parsed == null || !Number.isFinite(parsed)) && typeof raw === "string") {
    const asNumber = Number(raw.replace(",", "."));
    if (Number.isFinite(asNumber)) parsed = asNumber;
  }

  if (parsed == null || !Number.isFinite(parsed)) return false;

  // akceptuj zaokrąglenie do setnych
  return Math.abs(parsed - expected) <= 0.01;
}


// === answer (dokładnie) ===
export function renderAnswer({ data }) {
  const { b1Val, b2Val, angle } = data;
  const s = SIN_INFO[angle];

  // P = 1/2 * b1 * b2 * (num/den * sqrt(root?))
  // = (b1*b2*num)/(2*den) * sqrt(root?)
  const k = b1Val * b2Val * (s.num ?? 1);
  const den = 2 * (s.den ?? 1);
  const latexCoeff = formatCoeffWithRoot(k, den, s.root);

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`P = ${latexCoeff}`} />
    </div>
  );
}

// === explanation ===
export function renderExplanation({ data }) {
  const { b1Name, b2Name, angleName, b1Val, b2Val, angle } = data;
  const s = SIN_INFO[angle];

  const prod = b1Val * b2Val;

  const baseLatex = s.root
    ? `\\dfrac{\\sqrt{${s.root}}}{${s.den}}`
    : `\\dfrac{${s.num}}{${s.den}}`;

  const numer0 = prod * (s.num ?? 1);
  const denom0 = 2 * (s.den ?? 1);

  const unsimplified = s.root
    ? `\\dfrac{${prod}\\,\\sqrt{${s.root}}}{${2 * s.den}}`
    : `\\dfrac{${prod}}{${2 * s.den}}`;

  const simplified = formatCoeffWithRoot(numer0, denom0, s.root);

  const g = gcd(Math.abs(numer0), Math.abs(denom0));
  const changed = g > 1 || (!s.root && numer0 % denom0 !== 0);

  const isObtuse = angle > 90;
  const acute = isObtuse ? 180 - angle : angle;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>
        <strong>Krok 1.</strong> Korzystamy ze wzoru na pole trójkąta z dwoma bokami
        i kątem zawartym:
      </div>
      <BlockMath math={`P = \\tfrac{1}{2}\\,${b1Name}\\,${b2Name}\\,\\sin ${angleName}`} />

      <div>
        <strong>Krok 2.</strong> Podstawiamy dane:
      </div>
      <BlockMath
        math={`P = \\tfrac{1}{2}\\cdot ${b1Val}\\cdot ${b2Val}\\cdot \\sin ${angle}^\\circ`}
      />

      {isObtuse && (
        <>
          <div>
            <strong>Krok 3.</strong> Kąt{" "}
            <InlineMath math={`${angle}^\\circ`} /> jest rozwarty, więc korzystamy
            ze wzoru redukcyjnego:
          </div>
          <BlockMath math={`\\sin(180^\\circ - \\alpha) = \\sin \\alpha`} />
          <BlockMath
            math={`\\sin ${angle}^\\circ = \\sin(180^\\circ - ${angle}^\\circ)`}
          />
          <BlockMath math={`\\sin ${angle}^\\circ = \\sin ${acute}^\\circ`} />
          <BlockMath math={`\\sin ${acute}^\\circ = ${s.latex}`} />
        </>
      )}

      {!isObtuse && (
        <>
          <div>
            <strong>Krok 3.</strong> Odczytujemy dokładną wartość sinusa:
          </div>
          <BlockMath math={`\\sin ${angle}^\\circ = ${s.latex}`} />
        </>
      )}

      <div>
        <strong>Krok 4.</strong> Podstawiamy do wzoru i upraszczamy:
      </div>
      <BlockMath math={`P = \\tfrac{1}{2}\\cdot ${b1Val}\\cdot ${b2Val}\\cdot ${baseLatex}`} />

      {changed ? (
        <>
          <BlockMath math={`P = ${unsimplified}`} />
          <BlockMath math={`P = ${simplified}`} />
        </>
      ) : (
        <BlockMath math={`P = ${unsimplified}`} />
      )}
    </div>
  );
}



// === user answer ===
export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  if (!raw || typeof raw !== "string" || raw.trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }
  return (
    <div style={{ marginTop: 8 }}>
      <InlineMath math={formatLatexPreview(raw, "P")} />
    </div>
  );
}
